/**
 * Firebase Storage Utilities
 *
 * Provides Firebase Storage initialization and path utilities for
 * persisting chat attachments (PDFs, DOCXs) generated by agentic workflows.
 *
 * Storage path structure: users/{uid}/attachments/{messageId}/{filename}
 */

import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import app from './firebase';

// Initialize Firebase Storage
export const storage = getStorage(app);

/**
 * Sanitize a filename to be safe for Firebase Storage paths.
 * Removes or replaces characters that could cause path traversal or other issues.
 */
export function sanitizeFilename(filename: string): string {
  // Replace potentially problematic characters with underscores
  // Keep alphanumeric, dots, hyphens, and underscores
  return filename
    .replace(/[^a-zA-Z0-9.\-_]/g, '_')
    .replace(/_{2,}/g, '_') // Collapse multiple underscores
    .replace(/^_+|_+$/g, ''); // Trim leading/trailing underscores
}

/**
 * Generate the storage path for an attachment.
 *
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @param filename - Original filename
 * @returns Storage path string
 */
export function getAttachmentStoragePath(
  uid: string,
  messageId: string,
  filename: string
): string {
  const safeFilename = sanitizeFilename(filename);
  return `users/${uid}/attachments/${messageId}/${safeFilename}`;
}

/**
 * Get a Firebase Storage reference for an attachment.
 */
export function getAttachmentRef(
  uid: string,
  messageId: string,
  filename: string
) {
  const path = getAttachmentStoragePath(uid, messageId, filename);
  return ref(storage, path);
}

// Re-export commonly used functions for convenience
export { ref, uploadBytes, getDownloadURL, deleteObject };
